import { useState } from "react";

function SortIcon({ active, direction, hovered }) {
  const on  = "#ED701D";
  const off = "#9CA3AF";

  const s = { marginLeft: 4, flexShrink: 0, display: "inline-block", verticalAlign: "middle" };
  const gc = hovered ? on : off;

  if (active && direction === "asc") {
    return (
      <svg width="14" height="12" viewBox="0 0 14 12" style={s}>
        <path d="M7 0L4 4L10 4Z" fill={on} />
        <rect x="6" y="4" width="2" height="8" rx="0.5" fill={on} />
      </svg>
    );
  }

  if (active && direction === "desc") {
    return (
      <svg width="14" height="12" viewBox="0 0 14 12" style={s}>
        <rect x="6" y="0" width="2" height="8" rx="0.5" fill={on} />
        <path d="M7 12L4 8L10 8Z" fill={on} />
      </svg>
    );
  }

  return (
    <svg width="14" height="12" viewBox="0 0 14 12" style={s}>
      <path d="M3 0L0 4L6 4Z" fill={gc} />
      <rect x="2" y="4" width="2" height="8" rx="0.5" fill={gc} />
      <rect x="10" y="0" width="2" height="8" rx="0.5" fill={gc} />
      <path d="M11 12L8 8L14 8Z" fill={gc} />
    </svg>
  );
}

function Badge({ value }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "4px 12px", borderRadius: 20,
      fontSize: "0.7rem", fontWeight: 600,
      background: value ? "#FEE2E2" : "#D1FAE5",
      color:      value ? "#EF4444" : "#10B981",
    }}>
      <span style={{ fontSize: "0.65rem" }}>{value ? "✕" : "✓"}</span>
      {value ? "SIM" : "NÃO"}
    </span>
  );
}

function parseDateForSort(str) {
  if (!str) return 0;
  const [d, m, y] = str.split("/");
  return new Date(y, m - 1, d).getTime();
}

const formatCurrency = (v) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency", currency: "BRL",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

const MAIN_COLS = [
  { key: "weeklyPurchases", label: "COMPRAS\n(ÚLTIMA SEMANA)" },
  { key: "lastPurchaseDate", label: "DATA DA\nÚLTIMA COMPRA" },
  { key: "pendingPurchases", label: "COMPRAS\nPENDENTES" },
  { key: "pendingValue",     label: "VALOR DAS COMPRAS\nPENDENTES" },
];

const GROUP_COLS = [
  { key: "deliveryTimePending", label: "TEMPO DE ENTREGA" },
  { key: "minStockPending",     label: "ESTOQUE IDEAL" },
];

export function PurchaseOperationsTable({ stores }) {
  const [sort, setSort]           = useState({ column: null, direction: "asc" });
  const [hoveredCol, setHoveredCol] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);

  const handleSort = (col) => {
    setSort((prev) => {
      if (prev.column === col) {
        return { column: col, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { column: col, direction: "asc" };
    });
  };

  const sorted = [...stores].sort((a, b) => {
    if (!sort.column) return 0;
    let av = a[sort.column];
    let bv = b[sort.column];
    if (sort.column === "lastPurchaseDate") { av = parseDateForSort(av); bv = parseDateForSort(bv); }
    if (typeof av === "boolean")            { av = av ? 1 : 0;           bv = bv ? 1 : 0; }
    if (av < bv) return sort.direction === "asc" ? -1 : 1;
    if (av > bv) return sort.direction === "asc" ?  1 : -1;
    return 0;
  });

  const totals = stores.reduce(
    (acc, s) => ({
      weekly:   acc.weekly   + s.weeklyPurchases,
      pending:  acc.pending  + s.pendingPurchases,
      value:    acc.value    + s.pendingValue,
      delivery: acc.delivery + (s.deliveryTimePending ? 1 : 0),
      minStock: acc.minStock + (s.minStockPending     ? 1 : 0),
    }),
    { weekly: 0, pending: 0, value: 0, delivery: 0, minStock: 0 }
  );

  const thBase = {
    padding: "10px 14px",
    fontSize: "0.67rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    background: "#F3F4F6",
    textAlign: "center",
    border: "none",
    userSelect: "none",
  };

  const tdBase = {
    padding: "11px 14px",
    fontSize: "0.8rem",
    textAlign: "center",
    borderBottom: "1px solid #F3F4F6",
    color: "#374151",
  };

  const tfBase = {
    padding: "12px 14px",
    fontSize: "0.82rem",
    textAlign: "center",
    borderTop: "2px solid #FED7AA",
    fontWeight: 700,
    color: "#374151",
  };

  const renderTh = (col, extraStyle = {}) => {
    const isActive  = sort.column === col.key;
    const isHovered = hoveredCol  === col.key;
    return (
      <th
        key={col.key}
        style={{
          ...thBase,
          cursor: "pointer",
          whiteSpace: "pre-line",
          color: isActive || isHovered ? "#ED701D" : "#6B7280",
          ...extraStyle,
        }}
        onClick={() => handleSort(col.key)}
        onMouseEnter={() => setHoveredCol(col.key)}
        onMouseLeave={() => setHoveredCol(null)}
      >
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          {col.label}
          <SortIcon active={isActive} direction={sort.direction} hovered={isHovered} />
        </span>
      </th>
    );
  };

  return (
    <div style={{
      overflowX: "auto",
      borderRadius: 12,
      border: "1px solid #F3F4F6",
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    }}>
      <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>

        <thead>
          <tr>
            <th rowSpan={2} style={{
              ...thBase,
              textAlign: "center",
              verticalAlign: "middle",
              color: "#6B7280",
              borderRight: "1px solid #E5E7EB",
              borderBottom: "1px solid #E5E7EB",
            }}>
              LOJA
            </th>

            {MAIN_COLS.map((col) => {
              const isActive  = sort.column === col.key;
              const isHovered = hoveredCol  === col.key;
              return (
                <th
                  key={col.key}
                  rowSpan={2}
                  style={{
                    ...thBase,
                    cursor: "pointer",
                    whiteSpace: "pre-line",
                    verticalAlign: "middle",
                    color: isActive || isHovered ? "#ED701D" : "#6B7280",
                    borderRight: "1px solid #E5E7EB",
                    borderBottom: "1px solid #E5E7EB",
                  }}
                  onClick={() => handleSort(col.key)}
                  onMouseEnter={() => setHoveredCol(col.key)}
                  onMouseLeave={() => setHoveredCol(null)}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    {col.label}
                    <SortIcon active={isActive} direction={sort.direction} hovered={isHovered} />
                  </span>
                </th>
              );
            })}

            <th colSpan={2} style={{
              ...thBase,
              color: "#6B7280",
              borderBottom: "1px solid #E5E7EB",
            }}>
              PENDÊNCIAS DE CADASTROS
            </th>
          </tr>

          <tr>
            {renderTh(GROUP_COLS[0], { borderRight: "1px solid #E5E7EB", borderBottom: "1px solid #E5E7EB" })}
            {renderTh(GROUP_COLS[1], { borderBottom: "1px solid #E5E7EB" })}
          </tr>
        </thead>

        <tbody>
          {sorted.map((store, idx) => {
            const isHovered = hoveredRow === idx;
            const bg = isHovered
              ? "#FFF0F0"                          
              : idx % 2 === 0 ? "#FFFFFF" : "#F9FAFB"; 

            return (
              <tr
                key={store.name}
                style={{ background: bg, transition: "background 0.12s" }}
                onMouseEnter={() => setHoveredRow(idx)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td style={{ ...tdBase, textAlign: "left", paddingLeft: 20, fontWeight: 500, borderRight: "1px solid #F3F4F6" }}>
                  {store.name}
                </td>
                <td style={{ ...tdBase, borderRight: "1px solid #F3F4F6" }}>
                  {store.weeklyPurchases}
                </td>
                <td style={{ ...tdBase, color: "#9CA3AF", borderRight: "1px solid #F3F4F6" }}>
                  {store.lastPurchaseDate}
                </td>
                <td style={{ ...tdBase, color: "#ED701D", fontWeight: 600, borderRight: "1px solid #F3F4F6" }}>
                  {store.pendingPurchases}
                </td>
                <td style={{ ...tdBase, color: "#ED701D", fontWeight: 600, borderRight: "1px solid #F3F4F6" }}>
                  {formatCurrency(store.pendingValue)}
                </td>
                <td style={{ ...tdBase, borderRight: "1px solid #F3F4F6" }}>
                  <Badge value={store.deliveryTimePending} />
                </td>
                <td style={tdBase}>
                  <Badge value={store.minStockPending} />
                </td>
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          <tr style={{ background: "#FFF7ED" }}>
            <td style={{ ...tfBase, textAlign: "left", paddingLeft: 20, borderRight: "1px solid #FED7AA" }}>
              Total Geral
            </td>
            <td style={{ ...tfBase, borderRight: "1px solid #FED7AA" }}>{totals.weekly}</td>
            <td style={{ ...tfBase, color: "#9CA3AF", borderRight: "1px solid #FED7AA" }}>—</td>
            <td style={{ ...tfBase, color: "#ED701D", borderRight: "1px solid #FED7AA" }}>{totals.pending}</td>
            <td style={{ ...tfBase, color: "#ED701D", borderRight: "1px solid #FED7AA" }}>{formatCurrency(totals.value)}</td>
            <td style={{ ...tfBase, borderRight: "1px solid #FED7AA" }}>{totals.delivery} / {stores.length}</td>
            <td style={tfBase}>{totals.minStock} / {stores.length}</td>
          </tr>
        </tfoot>

      </table>
    </div>
  );
}
